/**
 * `BreadcrumbList` JSON-LD (structured-data task, 2026-09-27): tells a
 * crawler -- especially an AI crawler, which does not run the client bundle
 * -- where a page sits in the site hierarchy, from data the page already
 * shows. It reads the SAME redux state `Breadcrumbs.jsx` renders
 * (`state.breadcrumbs.items` / `.root`, both already `flattenToAppURL`'d by
 * core's reducer -- see `reducers/breadcrumbs/breadcrumbs.js`), so this can
 * never assert a trail the visible breadcrumb bar does not also show.
 *
 * Absolute URLs from `SiteConfig.baseUrl`, same reasoning as `DocumentSEO`
 * and `BrowseSEO`: identical on the server and the client, independent of
 * the request host.
 *
 * Mounted sitewide as an appExtra with `match: ''` (index.ts), beside
 * FeedDiscovery and NoIndexRoutes. That is safe on every route because this
 * emits nothing where there is no trail: the site root, and any route whose
 * breadcrumbs slice is empty.
 */
import React from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Helmet } from '@plone/volto/helpers';
import { useSiteConfig } from '../../helpers/useSiteConfig';
import { absoluteUrl } from '../Browse/browseState';

/**
 * `{title, url}` crumbs (the trail Volto's own `Breadcrumbs.jsx` renders,
 * current page last) -> a `BreadcrumbList`. `root` stands for the
 * still-unnamed "Home" crumb `Breadcrumbs.jsx` renders as its home icon,
 * which is not itself part of `items` -- see
 * `customizations/components/theme/Breadcrumbs/Breadcrumbs.jsx`.
 *
 * Null with no site origin, or when there is nothing below Home to show: a
 * one-crumb "Home" list is not a trail, and the front page itself has none.
 */
export const breadcrumbListJsonLd = (site, root, items) => {
  const origin = (site?.baseUrl || '').replace(/\/+$/, '');
  if (!origin || !Array.isArray(items) || items.length === 0) return null;

  const trail = [{ title: 'Home', url: root || '/' }, ...items];

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.title,
      item: absoluteUrl(site, crumb.url),
    })),
  };
};

const BreadcrumbJsonLd = () => {
  const site = useSiteConfig();
  const items = useSelector((state) => state.breadcrumbs.items, shallowEqual);
  const root = useSelector((state) => state.breadcrumbs.root);
  const jsonld = breadcrumbListJsonLd(site, root, items);
  if (!jsonld) return null;
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonld)}</script>
    </Helmet>
  );
};

export default BreadcrumbJsonLd;
